import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { catchError, Observable, throwError } from 'rxjs';
import { AuthCredential } from '../models/auth-data.model';
import { environment } from 'environments/environment';
import { UserDataService } from '../datas/user-data';
import { SessionExpiryService } from './session-expiry.service';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const userDataService = inject(UserDataService);
    const sessionExpiryService = inject(SessionExpiryService);

    // Clone the request object
    let newReq = req.clone();

    // Request
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.
    if (authService.accessToken && !authService.isTokenExpired()) {
        if (!req.url.includes('amazonaws') && !req.url.includes('storage.googleapis') && !req.url.includes('assets')) {
            newReq = req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + authService.accessToken),
            });
        }
    }

    if (!req.url.includes('auth/token/') && !req.url.includes('auth/logout') && !req.url.includes('auth/revoke-token')) {
        let params = req.params.set(
            "app_id",
            environment.appUUID
        );

        if (environment.projectFix && environment.projectId > 0) {
            params = params.set(
                "project_id",
                environment.projectId
            );
        } else {
            // Check if we are in a project path and project_id isn't alread set in query
            if (!params.has('project_id')) {
                let projectIdSet = false;

                // Priority 1: If project_owner, try to get from active project
                if (authService.hasBasicRole('project_owner')) {
                    const activeProject = userDataService.getProject();
                    if (activeProject && activeProject.id) {
                        params = params.set('project_id', activeProject.id);
                        projectIdSet = true;
                    }
                }

                // Priority 2: Regex match from URL
                if (!projectIdSet) {
                    const matches = router.url.match(/\/p\/(\d+)/);
                    if (matches && matches[1]) {
                        params = params.set('project_id', matches[1]);
                    }
                }
            }
        }

        newReq = newReq.clone({
            params: params,
        });
    }

    // Response
    return next(newReq).pipe(
        // Catch "401 Unauthorized" responses
        catchError((error) => {
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                let ignore401 = false;

                // If the URL is auth/token/
                if (req.url.includes('auth/token/')) {
                    const body = req.body as any;
                    // If grant_type is password, ignore 401 (it's just a wrong password)
                    if (body && body.grant_type === 'password') {
                        ignore401 = true;
                    }
                    // A failed refresh is handled by SessionExpiryService, which shows the
                    // expiry dialog. Logging out here would pre-empt it.
                    if (body && body.grant_type === 'refresh_token') {
                        ignore401 = true;
                    }
                }

                if (!ignore401) {
                    // Double check if token is expired, though 401 is the ultimate source of truth
                    const isExpired = authService.isTokenExpired();
                    console.log(`[AuthInterceptor] 401 detected. Token expired? ${isExpired}. Logging out...`);

                    // Tear down the expiry watchdog / dialog before signing out
                    sessionExpiryService.stop();

                    // Sign out, then reload the app (once the Firebase session is gone too)
                    authService.signOut().subscribe(() => location.reload());
                }
            }

            return throwError(() => error);
        }),
    );
};
